import React, { useState, useEffect, useContext } from "react";
import {
  Snackbar,
  SnackbarContent,
  IconButton,
  LinearProgress,
  Typography,
  Box,
  makeStyles,
  Collapse,
  useTheme
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon
} from "@material-ui/icons";
import { green, red, orange } from "@material-ui/core/colors";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  snackbar: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    maxWidth: 400,
    minWidth: 300,
    zIndex: 9999, // Garantir que fique acima de outros elementos
    position: 'fixed'
  },
  content: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8]
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  title: {
    flex: 1,
    fontWeight: "bold",
    fontSize: "0.95rem"
  },
  message: {
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  details: {
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontStyle: "italic"
  },
  progressBar: {
    marginTop: theme.spacing(1),
    height: 6,
    borderRadius: 3
  },
  successIcon: {
    color: green[500]
  },
  errorIcon: {
    color: red[500]
  },
  processingIcon: {
    color: orange[500]
  },
  progressContainer: {
    marginTop: theme.spacing(1)
  }
}));

const CompanyDeletionProgress = () => {
  const classes = useStyles();
  const theme = useTheme();
  const authContext = useContext(AuthContext);
  const socket = authContext?.socket;
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!socket) {
      console.log('[CompanyDeletionProgress] Socket não disponível ainda');
      return;
    }

    console.log('[CompanyDeletionProgress] Socket disponível, registrando listeners');

    const handleProgress = (data) => {
      console.log('[CompanyDeletionProgress] Evento recebido:', data);
      
      setNotifications((prev) => {
        // Procurar notificação existente da mesma empresa
        const existingIndex = prev.findIndex(n => n.companyId === data.companyId);
        
        const notificationData = {
          companyId: data.companyId,
          step: data.step,
          progress: data.progress || 0,
          message: data.message,
          details: data.details,
          timestamp: data.timestamp,
          success: data.success,
          error: data.error,
          open: true
        };
        
        if (existingIndex >= 0) {
          // Atualizar notificação existente
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...notificationData,
            id: updated[existingIndex].id // Manter o mesmo ID
          };
          console.log('[CompanyDeletionProgress] Atualizando notificação existente:', updated[existingIndex]);
          return updated;
        } else {
          // Criar nova notificação
          const newNotification = {
            id: `company-${data.companyId}`,
            ...notificationData
          };
          console.log('[CompanyDeletionProgress] Criando nova notificação:', newNotification);
          return [...prev, newNotification];
        }
      });

      // Se for sucesso ou erro, mostrar toast também
      if (data.step === 'complete' && data.success) {
        setTimeout(() => {
          toast.success(`Empresa excluída com sucesso!`);
          // Disparar evento customizado para atualizar listas
          window.dispatchEvent(new CustomEvent('company-deleted', { 
            detail: { companyId: data.companyId } 
          }));
        }, 500);
      } else if (data.step === 'error' || (data.success === false)) {
        setTimeout(() => {
          toast.error(`Erro ao excluir empresa: ${data.error || data.message}`);
        }, 500);
      }
    };

    // Escutar eventos de progresso de exclusão
    // O backend emite evento global "company-deletion-progress" com os dados
    console.log('[CompanyDeletionProgress] Registrando listener para company-deletion-progress');
    
    // Verificar se o socket está conectado
    if (socket.connected) {
      console.log('[CompanyDeletionProgress] Socket está conectado');
      socket.on("company-deletion-progress", handleProgress);
    } else {
      console.warn('[CompanyDeletionProgress] Socket NÃO está conectado! Aguardando conexão...');
      const connectHandler = () => {
        console.log('[CompanyDeletionProgress] Socket conectado agora, registrando listener');
        socket.on("company-deletion-progress", handleProgress);
      };
      socket.on('connect', connectHandler);
      
      // Se já estiver conectado quando o handler for adicionado, registrar imediatamente
      if (socket.connected) {
        connectHandler();
      }
    }

    return () => {
      console.log('[CompanyDeletionProgress] Removendo listeners');
      socket.off("company-deletion-progress", handleProgress);
      socket.off('connect');
    };
  }, [socket]);

  const handleClose = (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, open: false } : n
      )
    );

    // Remover após animação
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    }, 300);
  };

  const getIcon = (notification) => {
    if (notification.step === 'complete' && notification.success) {
      return <CheckCircleIcon className={classes.successIcon} />;
    }
    if (notification.step === 'error' || notification.success === false) {
      return <ErrorIcon className={classes.errorIcon} />;
    }
    return <DeleteIcon className={classes.processingIcon} />;
  };

  // Escutar evento customizado quando uma exclusão é iniciada
  useEffect(() => {
    const handleDeletionStarted = (event) => {
      const { companyId, companyName, estimatedRecords } = event.detail;
      console.log('[CompanyDeletionProgress] Exclusão iniciada via evento customizado:', event.detail);
      
      setNotifications((prev) => {
        // Verificar se já existe notificação para esta empresa
        const existingIndex = prev.findIndex(n => n.companyId === companyId);
        
        const notificationData = {
          id: `company-${companyId}`,
          companyId,
          step: 'init',
          progress: 0,
          message: `Exclusão iniciada: ${companyName}`,
          details: `Registros estimados: ${estimatedRecords}`,
          timestamp: new Date().toISOString(),
          success: undefined,
          error: undefined,
          open: true
        };
        
        if (existingIndex >= 0) {
          // Atualizar notificação existente
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...notificationData };
          return updated;
        } else {
          // Criar nova notificação
          return [...prev, notificationData];
        }
      });
    };

    window.addEventListener('company-deletion-started', handleDeletionStarted);

    return () => {
      window.removeEventListener('company-deletion-started', handleDeletionStarted);
    };
  }, []);

  // Debug: log das notificações
  useEffect(() => {
    console.log('[CompanyDeletionProgress] Notificações atuais:', notifications);
  }, [notifications]);

  // Debug: verificar se há notificações
  useEffect(() => {
    if (notifications.length === 0) {
      console.log('[CompanyDeletionProgress] Nenhuma notificação para exibir');
    } else {
      console.log('[CompanyDeletionProgress] Total de notificações:', notifications.length);
      notifications.forEach((n, i) => {
        console.log(`[CompanyDeletionProgress] Notificação ${i + 1}:`, {
          id: n.id,
          companyId: n.companyId,
          step: n.step,
          progress: n.progress,
          open: n.open,
          message: n.message
        });
      });
    }
  }, [notifications]);

  return (
    <>
      {notifications.length > 0 && (
        <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, pointerEvents: 'none' }}>
          {/* Container para debug - não interfere na renderização */}
        </div>
      )}
      {notifications.map((notification) => {
        console.log('[CompanyDeletionProgress] Renderizando notificação:', notification.id, 'open:', notification.open);
        return (
        <Snackbar
          key={notification.id}
          open={notification.open}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          className={classes.snackbar}
          TransitionComponent={Collapse}
          style={{ zIndex: 9999 }}
        >
          <SnackbarContent
            className={classes.content}
            message={
              <Box>
                <Box className={classes.header}>
                  {getIcon(notification)}
                  <Typography className={classes.title}>
                    Excluindo Empresa
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleClose(notification.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                {notification.step === 'deleting' && notification.message?.includes('Excluindo:') ? (
                  <>
                    <Box style={{ marginTop: 8, padding: 12, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)', borderRadius: 6, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'}` }}>
                      <Typography variant="caption" style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, color: theme.palette.mode === 'dark' ? '#ff9800' : '#f57c00' }}>
                        🔄 Operação em andamento:
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {notification.message.replace('Excluindo: ', '')}
                      </Typography>
                      {notification.details && (
                        <Typography variant="caption" style={{ display: 'block', marginTop: 4, color: theme.palette.text.secondary }}>
                          {notification.details}
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography className={classes.message}>
                      {notification.message}
                    </Typography>
                    
                    {notification.details && (
                      <Typography className={classes.details}>
                        {notification.details}
                      </Typography>
                    )}
                  </>
                )}
                
                {notification.step !== 'complete' && notification.step !== 'error' && (
                  <Box className={classes.progressContainer}>
                    <LinearProgress
                      variant="determinate"
                      value={notification.progress}
                      className={classes.progressBar}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {notification.progress}%
                    </Typography>
                  </Box>
                )}
              </Box>
            }
          />
        </Snackbar>
        );
      })}
    </>
  );
};

export default CompanyDeletionProgress;

